// Stock Transfers - Step 1: Select Items
import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Info,
  Calendar,
  FileText,
  Save,
  Search,
  Package,
  MapPin,
  Filter,
  Warehouse,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { format } from "date-fns";
import { mockStockItems } from "@/data/mockStockItems";

export interface TransferSetupData {
  reference: string;
  date: string;
  notes: string;
  transferMethod: "item" | "location";
  sourceLocation?: string; // Pre-filled source location for "location" mode
  selectedItems: Array<{
    id: string;
    code: string;
    name: string;
    availableQuantity: number;
  }>;
}

interface StockTransfersStep1PageProps {
  pageParams?: {
    prepopulatedItems?: Array<{
      stockCode: string;
      stockName: string;
      location?: string;
      warehouse?: string;
      quantity?: number;
    }>;
  };
}

export function StockTransfersStep1Page({ pageParams }: StockTransfersStep1PageProps = {}) {
  const { navigateTo } = useNavigation();
  const [searchQuery, setSearchQuery] = useState("");
  const [transferMode, setTransferMode] = useState<"item" | "location">("item");
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("WH-A"); // Default to Warehouse A
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [formData, setFormData] = useState<TransferSetupData>({
    reference: `TRF-${format(new Date(), "yyyy-MM-dd")}-${Math.floor(Math.random() * 1000).toString().padStart(3, "0")}`,
    date: format(new Date(), "yyyy-MM-dd"),
    notes: "",
    selectedItems: [],
  });

  // Check for pre-populated items from stock-items page
  useEffect(() => {
    if (pageParams?.prepopulatedItems && pageParams.prepopulatedItems.length > 0) {
      console.log('✅ [Step 1] Received prepopulated items:', pageParams.prepopulatedItems);
      
      // Convert prepopulated items to the format expected by Step 1
      const selectedItems = pageParams.prepopulatedItems.map((item) => ({
        id: item.stockCode,
        code: item.stockCode,
        name: item.stockName,
        availableQuantity: item.quantity || 0,
      }));
      
      console.log('📦 [Step 1] Selected items:', selectedItems);
      
      // Update form data with prepopulated items
      const firstItemLocation = pageParams.prepopulatedItems[0]?.location;
      setFormData(prev => ({
        ...prev,
        selectedItems: selectedItems,
        notes: firstItemLocation ? `Transfer from ${firstItemLocation}` : prev.notes,
      }));
      
      // Show success message
      toast.success(`Pre-filled ${selectedItems.length} item(s) for transfer`);
    }
  }, [pageParams]);

  // Check for pre-filled data from location slide-out
  useEffect(() => {
    const prefilledData = sessionStorage.getItem('prefilledTransfer');
    
    console.log('🔍 [Step 1] Checking for prefilled data:', prefilledData);
    
    if (prefilledData) {
      try {
        const parsed = JSON.parse(prefilledData);
        console.log('✅ [Step 1] Parsed prefilled data:', parsed);
        
        // Convert pre-filled items to the format expected by Step 1
        const selectedItems = parsed.items.map((item: any) => ({
          id: item.code,
          code: item.code,
          name: item.description,
          availableQuantity: item.quantity,
        }));
        
        console.log('📦 [Step 1] Selected items:', selectedItems);
        
        // Update form data with pre-filled items
        setFormData(prev => ({
          ...prev,
          selectedItems: selectedItems,
          notes: `Transfer from ${parsed.sourceLocation}`
        }));
        
        // Show success message
        toast.success(`Pre-filled ${selectedItems.length} item(s) from ${parsed.sourceLocation}`);
        
        // DON'T clear sessionStorage here - Step 2 needs it for source location
        // sessionStorage will be cleared in Step 2 after it uses the source location
      } catch (error) {
        console.error('❌ [Step 1] Error parsing prefilled transfer data:', error);
      }
    }
  }, []);

  // Get unique warehouses from stock items
  const warehouses = useMemo(() => {
    const uniqueWarehouses = new Map<string, string>();
    mockStockItems.forEach((item) => {
      if (!uniqueWarehouses.has(item.warehouseCode)) {
        // Map warehouse codes to friendly names
        const warehouseNames: { [key: string]: string } = {
          "WH-A": "Warehouse A",
          "WH-B": "Warehouse B",
          "WH-C": "Warehouse C",
          "RS": "Retail Store",
          "DC": "Distribution Center",
          "SR": "Storage Room",
        };
        uniqueWarehouses.set(item.warehouseCode, warehouseNames[item.warehouseCode] || item.warehouseCode);
      }
    });
    return Array.from(uniqueWarehouses.entries()).map(([code, name]) => ({ code, name }));
  }, []);

  // Get locations for the selected warehouse
  const locationsInWarehouse = useMemo(() => {
    if (!selectedWarehouse) return [];
    const locations = new Set<string>();
    mockStockItems.forEach((item) => {
      if (item.warehouseCode === selectedWarehouse) {
        locations.add(item.locationCode);
      }
    });
    return Array.from(locations).sort();
  }, [selectedWarehouse]);

  // Get items from the selected location
  const itemsInLocation = useMemo(() => {
    if (!selectedLocation) return [];
    
    // If "All Locations" is selected, return all items from the warehouse
    if (selectedLocation === "ALL_LOCATIONS") {
      return mockStockItems.filter(
        (item) =>
          item.warehouseCode === selectedWarehouse &&
          !item.inactive
      );
    }
    
    // Otherwise, return items from the specific location
    return mockStockItems.filter(
      (item) =>
        item.warehouseCode === selectedWarehouse &&
        item.locationCode === selectedLocation &&
        !item.inactive
    );
  }, [selectedWarehouse, selectedLocation]);

  // Reset location when warehouse changes
  useEffect(() => {
    setSelectedLocation("");
  }, [selectedWarehouse]);

  // Filter stock items based on search and location (for item mode)
  const filteredItems = useMemo(() => {
    let items = mockStockItems;
    
    // Apply search filter (includes code, name, category, and location)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(
        (item) =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          item.category?.toLowerCase().includes(query) ||
          // Search through all location quantities
          (item.locationQuantities && item.locationQuantities.some(loc => 
            loc.warehouseName?.toLowerCase().includes(query) ||
            loc.warehouseCode?.toLowerCase().includes(query) ||
            loc.locationName?.toLowerCase().includes(query) ||
            loc.locationCode?.toLowerCase().includes(query)
          ))
      );
    }
    
    return items;
  }, [searchQuery]);

  const handleItemToggle = (item: typeof mockStockItems[0], checked: boolean) => {
    if (checked) {
      setFormData({
        ...formData,
        selectedItems: [
          ...formData.selectedItems,
          {
            id: item.code, // Use code as ID
            code: item.code,
            name: item.description,
            availableQuantity: item.physicalQuantity || 0,
          },
        ],
      });
    } else {
      setFormData({
        ...formData,
        selectedItems: formData.selectedItems.filter((i) => i.id !== item.code),
      });
    }
  };

  const isItemSelected = (itemCode: string) => {
    return formData.selectedItems.some((i) => i.id === itemCode);
  };

  const handleSelectAll = () => {
    const allItems = filteredItems.map((item) => ({
      id: item.code,
      code: item.code,
      name: item.description,
      availableQuantity: item.physicalQuantity || 0,
    }));
    setFormData({
      ...formData,
      selectedItems: allItems,
    });
  };

  const handleDeselectAll = () => {
    setFormData({
      ...formData,
      selectedItems: [],
    });
  };

  const handleSelectAllFromLocation = () => {
    if (itemsInLocation.length === 0) return;
    
    const newItems = itemsInLocation.map((item) => ({
      id: item.code,
      code: item.code,
      name: item.description,
      availableQuantity: item.physicalQuantity || 0,
    }));

    // Add items that aren't already selected
    const existingCodes = new Set(formData.selectedItems.map((item) => item.code));
    const itemsToAdd = newItems.filter((item) => !existingCodes.has(item.code));

    setFormData({
      ...formData,
      selectedItems: [...formData.selectedItems, ...itemsToAdd],
    });

    toast.success(`Added ${itemsToAdd.length} item(s) from location`);
  };

  const handleNext = () => {
    // Validation
    if (!formData.reference.trim()) {
      toast.error("Please enter a transfer reference");
      return;
    }
    if (!formData.date) {
      toast.error("Please select a transfer date");
      return;
    }
    if (formData.selectedItems.length === 0) {
      toast.error("Please select at least one item to transfer");
      return;
    }

    // Additional validation for location mode
    if (transferMode === "location" && !selectedWarehouse) {
      toast.error("Please select a warehouse");
      return;
    }

    // Navigate to step 2 with form data including transfer method and source location
    // Don't pass "ALL_LOCATIONS" as sourceLocation - let Step 2 handle individual locations
    const setupData: TransferSetupData = {
      ...formData,
      transferMethod: transferMode,
      sourceLocation: transferMode === "location" && selectedLocation !== "ALL_LOCATIONS" ? selectedLocation : undefined,
    };
    
    navigateTo("stock-transfers/create/step2", { setupData });
  };

  const handleSaveAndExit = () => {
    // Validation
    if (!formData.reference.trim()) {
      toast.error("Please enter a transfer reference before saving");
      return;
    }
    if (!formData.date) {
      toast.error("Please select a transfer date before saving");
      return;
    }

    // TODO: Save as draft
    toast.success("Transfer saved as draft! You can resume it later from the Stock Transfers page.");
    navigateTo("stock-transfers");
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 flex items-center justify-between pb-4 border-b bg-background">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateTo("stock-transfers")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Create Stock Transfer</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Transfer stock between locations
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto py-6">
        <div className="max-w-6xl mx-auto w-full space-y-6 ">
          {/* Progress Indicator */}
          <div className="mb-6">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
                  1
                </div>
                <span className="text-sm font-medium">Select Items</span>
              </div>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
                  2
                </div>
                <span className="text-sm text-muted-foreground">Configure Transfers</span>
              </div>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
                  3
                </div>
                <span className="text-sm text-muted-foreground">Review & Complete</span>
              </div>
            </div>
          </div>


          {/* Transfer Details - Horizontal Section */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle>Transfer Details</CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
          <div className="space-y-4">
            {/* Other Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Reference */}
              <div className="space-y-1.5">
                <Label htmlFor="reference" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Transfer Reference *
                </Label>
                <Input
                  id="reference"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="TRF-2024-001"
                  required
                />
              </div>

              {/* Date */}
              <div className="space-y-1.5">
                <Label htmlFor="date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Transfer Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes about this transfer..."
                />
              </div>
            </div>
          </div>
        </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Choose your transfer method and quickly select items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Transfer Method Selection */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ArrowRightLeft className="h-4 w-4" />
                    Transfer Method
                  </Label>
                  <Tabs 
                    value={transferMode} 
                    onValueChange={(value) => setTransferMode(value as "item" | "location")}
                    className="w-full"
                  >
                    <TabsList className="grid w-full max-w-md grid-cols-2">
                      <TabsTrigger value="item" className="gap-2">
                        <Package className="h-4 w-4" />
                        Transfer by Item
                      </TabsTrigger>
                      <TabsTrigger value="location" className="gap-2">
                        <MapPin className="h-4 w-4" />
                        Transfer by Location
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                {/* Quick Selection Buttons */}
                {transferMode === "item" && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={filteredItems.length === 0}
                    >
                      Select All Items
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleDeselectAll}
                      disabled={formData.selectedItems.length === 0}
                    >
                      Deselect All
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                💡 Tip: Use "Transfer by Location" to quickly select all items from a specific location
              </p>
            </CardContent>
          </Card>

          {/* Two Column Layout - Select Items & Selected Items */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Select Stock Items */}
        <Card className="h-full flex flex-col">
          <CardHeader>
            <div>
              <CardTitle>
                {transferMode === "item" ? "Select Stock Items" : "Select Location"}
              </CardTitle>
              <CardDescription>
                {transferMode === "item"
                  ? "Choose items to include in this transfer"
                  : "Select a warehouse and optionally a specific location to transfer items from"}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col space-y-4">
            {transferMode === "item" ? (
              <>
                {/* Search and Filter */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by code, name, category, or location..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button variant="outline" size="icon">
                    <Filter className="h-4 w-4" />
                    <span className="sr-only">Filter</span>
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Warehouse and Location Dropdowns */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="warehouse" className="flex items-center gap-2">
                      <Warehouse className="h-4 w-4" />
                      Warehouse *
                    </Label>
                    <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                      <SelectTrigger id="warehouse">
                        <SelectValue placeholder="Select warehouse" />
                      </SelectTrigger>
                      <SelectContent>
                        {warehouses.map((warehouse) => (
                          <SelectItem key={warehouse.code} value={warehouse.code}>
                            {warehouse.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      From Location
                    </Label>
                    <Select
                      value={selectedLocation}
                      onValueChange={setSelectedLocation}
                      disabled={!selectedWarehouse}
                    >
                      <SelectTrigger id="location">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL_LOCATIONS">All Locations</SelectItem>
                        {locationsInWarehouse.map((location) => (
                          <SelectItem key={location} value={location}>
                            {location}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedLocation && itemsInLocation.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllFromLocation}
                      className="w-full"
                    >
                      {selectedLocation === "ALL_LOCATIONS" 
                        ? `Select All Items from Warehouse (${itemsInLocation.length})`
                        : `Select All Items from Location (${itemsInLocation.length})`
                      }
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Items List */}
            <div className="flex-1 border rounded-lg overflow-hidden">
              <div className="max-h-[500px] overflow-y-auto">
                {(() => {
                  const itemsToShow = transferMode === "item" ? filteredItems : itemsInLocation;
                  const emptyMessage =
                    transferMode === "item"
                      ? searchQuery
                        ? "No items found matching your search"
                        : "No stock items available"
                      : !selectedLocation
                      ? "Please select a warehouse"
                      : selectedLocation === "ALL_LOCATIONS"
                      ? "No items found in this warehouse"
                      : "No items found in this location";

                  if (itemsToShow.length === 0) {
                    return (
                      <div className="p-8 text-center text-muted-foreground">
                        {emptyMessage}
                      </div>
                    );
                  }

                  return (
                    <div className="divide-y">
                      {itemsToShow.map((item) => (
                      <div
                        key={item.code}
                        className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                      >
                        <Checkbox
                          id={`item-${item.code}`}
                          checked={isItemSelected(item.code)}
                          onCheckedChange={(checked) =>
                            handleItemToggle(item, checked as boolean)
                          }
                        />
                        <label
                          htmlFor={`item-${item.code}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex items-start justify-between gap-4">
                            {/* Left side - Item info */}
                            <div className="flex-1 min-w-0">
                              {/* Top row: Code + Category */}
                              <div className="flex items-center gap-2 mb-1">
                                <Package className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium">{item.code}</span>
                                {item.category && (
                                  <Badge variant="outline" className="text-xs">
                                    {item.category}
                                  </Badge>
                                )}
                              </div>
                              {/* Middle row: Description */}
                              <div className="text-sm text-muted-foreground mb-2">
                                {item.description}
                              </div>
                              {/* Bottom row: Available quantity */}
                              <div className="text-sm font-medium">
                                Available: {item.physicalQuantity || 0} {item.unitOfMeasure || "units"}
                              </div>
                            </div>
                            {/* Right side - Locations listed vertically */}
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              {item.locationQuantities && item.locationQuantities.length > 0 ? (
                                item.locationQuantities.map((loc, idx) => (
                                  <div key={idx} className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                                    <MapPin className="h-3 w-3" />
                                    <span>{loc.warehouseName || loc.warehouseCode}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <MapPin className="h-3 w-3" />
                                  <span>No location</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </label>
                      </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right Column - Selected Items */}
        <Card className="h-full flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Selected Items</CardTitle>
              <Badge variant="secondary">
                {formData.selectedItems.length}
              </Badge>
            </div>
            <CardDescription>
              Items that will be included in this transfer
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            {formData.selectedItems.length > 0 ? (
              <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
                {formData.selectedItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-2 rounded border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Package className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{item.code}</span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground truncate">{item.name}</span>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs flex-shrink-0">
                          {item.availableQuantity}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 flex-shrink-0"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            selectedItems: formData.selectedItems.filter((i) => i.id !== item.id),
                          });
                        }}
                      >
                        <span className="sr-only">Remove</span>
                        <span className="text-xs">×</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <Package className="h-12 w-12 text-muted-foreground mb-3" />
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  No items selected yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Select items from the left panel to add them to your transfer
                </p>
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        </div>
      </div>

      {/* Sticky Action Bar */}
      <div className="flex border-t bg-background shadow-lg p-4 fixed bottom-3 px-4 lg:px-6 z-50">
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={() => navigateTo("stock-transfers")}
          >
            Cancel
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleSaveAndExit}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
            <Button onClick={handleNext} size="lg">
              Configure Transfers
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
